/* eslint-disable no-nested-ternary */
import { useCallback, useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Error } from "../../../../../../models/error";
import { Job } from "../../../../../../models/job";
import { Filters, Searches, Sort, Sorts } from "../../../../../../models/shipeezi.table";
import { StorageBox } from "../../../../../../models/storage.box";
import StorageBoxService from "../../../../../../services/storage.box.service";
import WarehousingService from "../../../../../../services/warehousing.service";
import { checkError } from "../../../../../../util/checkError";
import getRelatedFilters from "../../../../../../util/getRelatedFilters";
import { handlesGrid } from "../../../../../../util/handlesGrid";
import { CACHE_TIME } from "Util/constants";

export const useActions = () => {
  const { currentUser, warehouse } = useAppState();
  const { t: translate } = useTranslation();

  const [storageBoxesAndUnitsData, setStorageBoxesAndUnitsData] = useState<StorageBox[]>([]);

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [total, setTotal] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>();

  const { handleFilter } = handlesGrid({
    setFilters,
    setSorting,
    setPage,
    setPageSize,
    setSearches,
  });

  enum StorageBoxAndUnitsProperties {
    STORAGE_BOX_TYPE = "storageBoxType",
    STORAGE_STATUS = "storageStatus",
    ITEMS_JOBS = "itemsJobs",
    MAP_CELLS = "mapCells",
  }

  const verifyIsGetByJobOrCustomer = useCallback(() => {
    if (searches && searches?.length) {
      return !!searches.find((search) => Object.keys(search).includes("jobOrCustomer"));
    }

    return false;
  }, [searches]);

  const attributes = useMemo(
    () => ({
      itemsJobs: ["id"],
      job: ["id", "reference", "updatedAt", "sequence"],
      jobCustomer: ["fullName", "name", "lastName"],
      storageBoxType: ["name"],
      storageStatus: ["name"],
      warehouses: ["id", "name"],
      mapCells: ["name"],
    }),
    []
  );

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    if (!verifyIsGetByJobOrCustomer())
      sort.forEach((sort) => {
        switch (sort.columnName) {
          case StorageBoxAndUnitsProperties.STORAGE_STATUS:
            sorting.push([StorageBoxAndUnitsProperties.STORAGE_STATUS, "name", sort.direction]);
            break;
          case StorageBoxAndUnitsProperties.MAP_CELLS:
            sorting.push([StorageBoxAndUnitsProperties.MAP_CELLS, "name", sort.direction]);
            break;
          case StorageBoxAndUnitsProperties.STORAGE_BOX_TYPE:
            sorting.push([
              StorageBoxAndUnitsProperties.STORAGE_BOX_TYPE,
              "capacity",
              sort.direction,
            ]);
            break;
          case StorageBoxAndUnitsProperties.ITEMS_JOBS:
            sorting.push([
              StorageBoxAndUnitsProperties.ITEMS_JOBS,
              "job",
              "reference",
              sort.direction,
            ]);
            break;
          default:
            sorting.push([sort.columnName, sort.direction]);
        }
      });

    return sorting;
  };

  const getById = (id: number) =>
    useQuery(
      ["storage-box-get-by-id"],
      () =>
        StorageBoxService.getById(Number(id), {
          include: JSON.stringify([
            {
              association: "itemsJobs",
              attributes: attributes.itemsJobs,
              include: [
                {
                  association: "job",
                  attributes: attributes.job,
                  include: [{ association: "customer", attributes: attributes.jobCustomer }],
                },
              ],
            },
            { association: "storageBoxType", attributes: attributes.storageBoxType },
            { association: "storageStatus", attributes: attributes.storageStatus },
            { association: "mapCells", attributes: attributes.mapCells },
            {
              association: "jobs",
              attributes: attributes.job,
              include: { association: "customer", attributes: attributes.jobCustomer },
            },
          ]),
        }),
      {
        enabled: !!id,
        refetchOnWindowFocus: false,
        staleTime: 0,
        cacheTime: CACHE_TIME,
      }
    );

  const getStorageBoxesAndUnits = () =>
    useQuery(
      ["storage.boxes.and.units.list", page, pageSize, searches, filters, sorting, warehouse],
      () =>
        StorageBoxService.getAllUnits({
          page,
          pageSize,
          warehouseId: warehouse?.id,
          isFleet: 0,
          singleUse: 0,
          sortBy:
            sorting && sorting?.length > 0 && !verifyIsGetByJobOrCustomer()
              ? JSON.stringify([...sorting, ["updatedAt", "desc"]])
              : !verifyIsGetByJobOrCustomer()
              ? JSON.stringify([["updatedAt", "desc"]])
              : undefined,
          searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
          ...getRelatedFilters(filters, "storageBox", "status"),
          ...getRelatedFilters(filters, "storageStatus", "status"),
          ...getRelatedFilters(filters, "storageBoxType", "storage_box_type_id"),
        }),
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) {
            setTotal(response?.data.count || 0);
            setStorageBoxesAndUnitsData(response.data.rows);
          }
        },
      }
    );

  const useUnloadStorageBox = () => {
    return useMutation(
      ["storage.boxes.and.units.unload"],
      ({ storageBox, orders }: { storageBox: StorageBox; orders: Job[] }) =>
        WarehousingService.unloadAllByJobs({ storageBox, jobs: orders }),
      {
        onSuccess: () => {
          NotificationToast.success(translate("operationPerformedSuccessfully"), {
            id: "request.notification.success.storage.box.unload",
          });
          setHasError(false);
          refetchStorageBoxesAndUnits();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
          setHasError(true);
        },
      }
    );
  };

  const { refetch: refetchStorageBoxesAndUnits } = getStorageBoxesAndUnits();

  const handleSearch = (values: Searches) => {
    setSorting(undefined);
    setSearches(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setHasError(undefined);
    }
  }, [hasError]);

  return {
    page,
    pageSize,
    hasError,
    total,
    storageBoxesAndUnitsData,
    getById,
    setPageSize,
    useUnloadStorageBox,
    setSorting,
    handleSearch,
    handleSorting,
    handleFilter,
    handleCurrentPage,
    getStorageBoxesAndUnits,
    verifyIsGetByJobOrCustomer,
  };
};
